import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { BeegoController } from '../beego.controller';
import { Segment } from '@beego/ngx-segment';
import { getSegmentName, translateSegments } from '@shared-libs/helpers';
import { BeegoAppointmentProvider } from '@internal-providers/beego-appointment.provider';
import { BeegoTableFactory } from '@internal-factories/tables/beego-table.factory';
import { IDataQuery, TableDesktopComponent } from '@beego/ngx-table';
import { IBeegoAppointment } from '@internal-models/beego-appointment.model';
import { ActivatedRoute } from '@angular/router';
import { InternalNavigationType } from '@shared-directives/navigate.directive';
import { NavigationService } from '@shared-services/navigation.service';
import { Action, ExtBeegoAppointmentStatus, NavigationType } from '@shared-libs/enums';
import { BeegoAppointmentDetailsComponent } from '@internal-components/beego-appointment-components/beego-appointment-details-component/beego-appointment-details.component';
import { MessageService } from '@shared-services/message.service';
import { ClientProvider } from '@internal-providers/client.provider';
import { TenantProvider } from '@internal-providers/tenant.provider';

@Component({
	selector: 'beego-desktop-page',
	templateUrl: 'beego.desktop.page.html',
	styleUrls: ['./beego.desktop.page.scss'],
})
export class BeegoDesktopPage implements AfterViewInit, OnInit {
	@ViewChild(TableDesktopComponent) private readonly tableComponent: TableDesktopComponent;
	public readonly segments: Array<Segment> = translateSegments(['PLANNED', 'FINISHED', 'CANCELED']);
	public selectedSegment: string = getSegmentName(this.segments[0]); // default segment
	public getSegmentName = getSegmentName;

	public tableHeaders = BeegoTableFactory.getHeaders();
	public tableFilters = BeegoTableFactory.getFilters();

	public appointments: Array<IBeegoAppointment>;
	public tenantHasActiveBeegoLink: boolean = null;

	public readonly InternalNavigationType: typeof InternalNavigationType = InternalNavigationType;

	constructor(
		public readonly controller: BeegoController,
		public readonly provider: BeegoAppointmentProvider,
		private readonly clientProvider: ClientProvider,
		private readonly route: ActivatedRoute,
		private readonly navigationService: NavigationService,
		private readonly messageService: MessageService,
		private readonly tenantProvider: TenantProvider
	) {}

	public readonly dataRequestPlanned = (query: IDataQuery) =>
		this.provider.getAll(query, ExtBeegoAppointmentStatus.Open);
	public readonly dataRequestFinished = (query: IDataQuery) =>
		this.provider.getAll(query, ExtBeegoAppointmentStatus.Finished);
	public readonly dataRequestCanceled = (query: IDataQuery) =>
		this.provider.getAll(query, ExtBeegoAppointmentStatus.Canceled);

	public dataChanged(appointments: Array<IBeegoAppointment>): void {
		this.appointments = appointments;
	}

	public async ngOnInit(): Promise<void> {
		this.tenantHasActiveBeegoLink = await this.tenantProvider.hasBeegoPartnerLink();
	}

	public ngAfterViewInit(): void {
		this.route.url.subscribe(() => {
			this.tableComponent?.refreshData();
		});
	}

	public onSelectedSegmentChange(segment: string): void {
		this.selectedSegment = segment;
	}

	public async openAppointment(id: string) {
		const beegoAppointment: IBeegoAppointment = await this.provider.getOne(id);
		return this.navigationService.navigateTo(NavigationType.modal, BeegoAppointmentDetailsComponent, {
			beegoAppointment,
		});
	}

	public async openClient(id: string): Promise<void> {
		const client = await this.clientProvider.getOne(id);

		void this.navigationService
			.navigateTo<{ action: Action }>(NavigationType.page, '/clienten/client', {
				client,
			})
			.then((result) => {
				if (result?.action) {
					this.tableComponent?.refreshData();
					this.messageService.showSavedMessage();
				}
			})
			.catch();
	}
}
